import { Link, useParams } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as Avatar from '@givebutter/primitives/avatar';
import { Button } from '@givebutter/primitives/button';
import { faCode, faPage, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCampaign } from 'api/campaigns';
import * as Dialog from 'components/dialog';
import * as WidgetCard from 'components/widgets/WidgetCard';
import { WidgetLibraryInstallation } from 'components/widgets/WidgetLibraryInstallation';
import { WIDGETS_DOCS_LINK } from 'constants/links';
import { useCampaignWidgets } from 'customHooks/useCampaignWidgets';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInfiniteWidgets } from 'customHooks/useInfiniteWidgets';
import { useWidgetLibraryStatus } from 'customHooks/useWidgetLibraryStatus';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import WidgetInstallationImg from 'images/widgets-installation.svg?react';
import { campaignQueryKeys } from 'queries/campaign-query-keys';

const FETCH_LIMIT = 5;

const Widgets = () => {
    const params = useParams<{ campaign_id?: string }>();
    const campaignId = Number(params.campaign_id);
    const { hasWidgetsLibraryInstalled, showWidgets, setSkipWidgetLibraryInstallation } =
        useWidgetLibraryStatus();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useSuspenseQuery({
        queryKey: campaignQueryKeys.campaign(campaignId),
        queryFn: () => conditionallyFetchOrNull(() => fetchCampaign({ campaignId }), !!campaignId),
    });
    const { data: campaignWidgets } = useCampaignWidgets(campaignId);
    const { data: allWidgets, ref } = useInfiniteWidgets({ per_page: FETCH_LIMIT });
    const transformedAllWidgets = allWidgets?.pages ?? [];
    const widgets = campaignId ? campaignWidgets : transformedAllWidgets;
    const hasButton = widgets.filter(wid => wid.type === 'button').length > 0;
    const hasForm = widgets.filter(wid => wid.type === 'giving-form').length > 0;
    const hasGoalBar = widgets.filter(wid => wid.type === 'goal-bar').length > 0;

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="text-2xl font-medium">Widgets</div>
                    {hasWidgetsLibraryInstalled ? (
                        <span className="rounded-xl bg-green-50 px-2 py-1 text-xs font-medium text-green-500">
                            Library installed
                        </span>
                    ) : (
                        <span className="rounded-xl bg-red-50 px-2 py-1 text-xs font-medium text-red-500">
                            Library not installed
                        </span>
                    )}
                </div>
                <div className="flex gap-4">
                    {showWidgets && (
                        <Dialog.Root>
                            <Dialog.Trigger asChild>
                                <Button variant="transparent" size="sm">
                                    Installation
                                </Button>
                            </Dialog.Trigger>
                            <Dialog.Portal>
                                <Dialog.Overlay />
                                <Dialog.Content className="max-w-[40rem]">
                                    <div className="flex flex-col gap-4 p-6">
                                        <div className="text-xl font-medium">
                                            Select your platform to get started
                                        </div>
                                        <WidgetLibraryInstallation inPortal={true} />
                                    </div>
                                    <Dialog.Close />
                                </Dialog.Content>
                            </Dialog.Portal>
                        </Dialog.Root>
                    )}
                    <Button
                        variant="outline"
                        color="neutral"
                        onClick={() => window.open(WIDGETS_DOCS_LINK, '_blank')}
                        size="sm"
                    >
                        <FontAwesomeIcon icon={faPage} />
                        Read docs
                    </Button>
                    {showWidgets && !campaignId && (
                        <Link to={`/accounts/${accountId}/settings/integrations/widgets/config`}>
                            <Button variant="solid" size="sm">
                                <FontAwesomeIcon icon={faPlus} /> New widget
                            </Button>
                        </Link>
                    )}
                </div>
            </div>
            {!showWidgets && (
                <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                    <WidgetInstallationImg className="w-full" />
                    <WidgetLibraryInstallation inPortal={false} />
                    <div className="flex flex-col items-end text-sm font-medium">
                        <div className="text-gray-700">Don't need the tutorial?</div>
                        <div>
                            <Button
                                variant="none"
                                color="primary"
                                className="font-medium"
                                onClick={() => setSkipWidgetLibraryInstallation(true)}
                            >
                                View widgets
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {showWidgets && !campaignId && widgets.length === 0 && (
                <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white p-12 text-center text-sm shadow-sm">
                    <div className="flex max-w-xs flex-col items-center justify-center gap-3">
                        <Avatar.Root>
                            <FontAwesomeIcon icon={faCode} size="lg" />
                        </Avatar.Root>
                        <div>
                            <div className="font-medium">No widgets found</div>
                            <div>
                                Create your first widget and enjoy powerful fundraising right on
                                your website
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {widgets.map(widget => {
                    return (
                        <WidgetCard.Root
                            key={widget.display_id}
                            value={{ widget: { ...widget, campaign: campaign ?? widget.campaign } }}
                        >
                            <WidgetCard.Content />
                        </WidgetCard.Root>
                    );
                })}
                {!hasButton && showWidgets && !!campaignId && (
                    <WidgetCard.Root value={{ widget: { type: 'button', campaign } }}>
                        <WidgetCard.Content />
                    </WidgetCard.Root>
                )}
                {!hasForm && showWidgets && !!campaignId && (
                    <WidgetCard.Root
                        value={{
                            widget: { type: 'giving-form', campaign },
                        }}
                    >
                        <WidgetCard.Content />
                    </WidgetCard.Root>
                )}
                {!hasGoalBar && showWidgets && !!campaignId && (
                    <WidgetCard.Root
                        value={{
                            widget: { type: 'goal-bar', campaign },
                        }}
                    >
                        <WidgetCard.Content />
                    </WidgetCard.Root>
                )}
                <div ref={ref} />
            </div>
        </div>
    );
};

export { Widgets };
